import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calculator, ArrowRight, IndianRupee } from 'lucide-react';
import { Link } from 'react-router-dom';

const QuickQuoteCalculator = () => {
  const [beltType, setBeltType] = useState('');
  const [width, setWidth] = useState('');
  const [length, setLength] = useState('');
  const [quantity, setQuantity] = useState('');
  const [estimatedPrice, setEstimatedPrice] = useState(null);

  const beltTypes = [
    { name: 'Timing Belts', basePrice: 250 },
    { name: 'V-Belts', basePrice: 180 },
    { name: 'Flat Belts', basePrice: 200 },
    { name: 'Conveyor Belts', basePrice: 350 },
    { name: 'Poly-V Belts', basePrice: 220 }
  ];

  const calculatePrice = () => {
    if (!beltType || !width || !length || !quantity) {
      setEstimatedPrice(null);
      return;
    }

    const selectedBelt = beltTypes.find(b => b.name === beltType);
    if (!selectedBelt) return;

    const widthNum = parseFloat(width);
    const lengthNum = parseFloat(length);
    const quantityNum = parseInt(quantity);

    // Pricing formula: basePrice * (width/100) * (length/1000) * quantity
    const areaFactor = (widthNum / 100) * (lengthNum / 1000);
    const subtotal = selectedBelt.basePrice * areaFactor * quantityNum;

    // Volume discount
    let discount = 0;
    if (quantityNum >= 50) discount = 0.15;
    else if (quantityNum >= 20) discount = 0.10;
    else if (quantityNum >= 10) discount = 0.05;

    const total = subtotal * (1 - discount);

    setEstimatedPrice({
      subtotal: Math.round(subtotal),
      discount: discount * 100,
      total: Math.round(total),
      perUnit: Math.round(total / quantityNum)
    });
  };

  useEffect(() => {
    calculatePrice();
  }, [beltType, width, length, quantity]);

  return (
    <div className="glass-card p-8 max-w-4xl mx-auto" data-testid="quick-quote-calculator">
      <div className="flex items-center justify-center mb-6">
        <div className="w-12 h-12 bg-[#3B82F6] rounded-lg flex items-center justify-center mr-3">
          <Calculator className="text-white" size={24} />
        </div>
        <div>
          <h2 className="heading-font text-2xl text-[#1E293B]">QUICK QUOTE CALCULATOR</h2>
          <p className="text-[#64748B] text-sm">Get instant price estimates in seconds</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div>
          <Label htmlFor="quick-belt-type" className="text-[#1E293B] mb-2 block text-sm">Belt Type *</Label>
          <Select value={beltType} onValueChange={setBeltType}>
            <SelectTrigger id="quick-belt-type" data-testid="quick-belt-type" className="bg-white border-gray-300 text-[#1E293B]">
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent className="bg-white border-gray-300 text-[#1E293B]">
              {beltTypes.map((type) => (
                <SelectItem key={type.name} value={type.name}>{type.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="quick-width" className="text-[#1E293B] mb-2 block text-sm">Width (mm) *</Label>
          <Input
            id="quick-width"
            type="number"
            step="0.1"
            placeholder="e.g., 100"
            value={width}
            onChange={(e) => setWidth(e.target.value)}
            className="bg-white border-gray-300 text-[#1E293B]"
            data-testid="quick-width"
          />
        </div>

        <div>
          <Label htmlFor="quick-length" className="text-[#1E293B] mb-2 block text-sm">Length (mm) *</Label>
          <Input
            id="quick-length"
            type="number"
            step="0.1"
            placeholder="e.g., 1000"
            value={length}
            onChange={(e) => setLength(e.target.value)}
            className="bg-white border-gray-300 text-[#1E293B]"
            data-testid="quick-length"
          />
        </div>

        <div>
          <Label htmlFor="quick-quantity" className="text-[#1E293B] mb-2 block text-sm">Quantity *</Label>
          <Input
            id="quick-quantity"
            type="number"
            placeholder="e.g., 10"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            className="bg-white border-gray-300 text-[#1E293B]"
            data-testid="quick-quantity"
          />
        </div>
      </div>

      {estimatedPrice ? (
        <div className="bg-gradient-to-br from-[#EFF6FF] to-[#DBEAFE] p-6 rounded-lg border border-[#3B82F6]/20" data-testid="price-result">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
            <div className="text-center">
              <div className="text-[#64748B] text-xs mb-1">Subtotal</div>
              <div className="flex items-center justify-center text-[#1E293B] text-lg font-bold mono-font">
                <IndianRupee size={18} className="mr-1" />
                {estimatedPrice.subtotal.toLocaleString('en-IN')}
              </div>
            </div>

            {estimatedPrice.discount > 0 && (
              <div className="text-center">
                <div className="text-[#64748B] text-xs mb-1">Volume Discount</div>
                <div className="text-[#10B981] text-lg font-bold">
                  -{estimatedPrice.discount}%
                </div>
              </div>
            )}

            <div className="text-center">
              <div className="text-[#64748B] text-xs mb-1">Per Unit</div>
              <div className="flex items-center justify-center text-[#1E293B] text-lg font-bold mono-font">
                <IndianRupee size={18} className="mr-1" />
                {estimatedPrice.perUnit.toLocaleString('en-IN')}
              </div>
            </div>
          </div>

          <div className="border-t border-[#3B82F6]/20 pt-4 mb-4">
            <div className="flex items-center justify-between">
              <span className="heading-font text-[#1E293B] text-lg">ESTIMATED TOTAL</span>
              <div className="flex items-center text-[#3B82F6] text-3xl font-bold mono-font">
                <IndianRupee size={28} />
                {estimatedPrice.total.toLocaleString('en-IN')}
              </div>
            </div>
          </div>

          <div className="bg-white/80 p-4 rounded border border-[#3B82F6]/10">
            <p className="text-[#64748B] text-xs mb-3">
              * This is an estimated price. Final pricing may vary based on specifications, customization, and bulk discounts.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                asChild
                className="btn-accent flex-1"
                data-testid="proceed-to-rfq"
              >
                <Link to="/configure">
                  Proceed to Full RFQ <ArrowRight size={16} className="ml-2" />
                </Link>
              </Button>
              <Button
                variant="outline"
                className="btn-secondary flex-1"
                onClick={() => {
                  setBeltType('');
                  setWidth('');
                  setLength('');
                  setQuantity('');
                  setEstimatedPrice(null);
                }}
                data-testid="calculate-again"
              >
                Calculate Again
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200" data-testid="calculator-prompt">
          <Calculator className="text-[#94A3B8] mx-auto mb-3" size={40} />
          <p className="text-[#64748B] text-sm">
            Fill in all fields above to see your instant price estimate
          </p>
          <div className="mt-4 flex flex-wrap gap-2 justify-center text-xs">
            <span className="px-3 py-1 bg-white rounded-full text-[#64748B] border border-gray-200">
              5% off for 10+ units
            </span>
            <span className="px-3 py-1 bg-white rounded-full text-[#64748B] border border-gray-200">
              10% off for 20+ units
            </span>
            <span className="px-3 py-1 bg-white rounded-full text-[#64748B] border border-gray-200">
              15% off for 50+ units
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickQuoteCalculator;
